/**
 * `ResumeBanner` — "last track + position, one tap to continue" (`spec.md` §1.4, F13).
 *
 * The reconciled resume pointer (server, `CloudStorage`, `DeviceStorage`,
 * `localStorage` — newest `at` wins, §13.7) arrives long before the user has touched
 * anything, and the app may not act on it: playback restrictions are cleared per
 * element by a **gesture**, so a resume has to be offered, not performed (§13.2). This
 * is that offer.
 *
 * It is a state, so it follows the state rules: the real grid, one silkscreen label,
 * the track, the position, and one action. No illustration, no dialog, nothing modal —
 * the banner sits at the top of the crate and the user may simply scroll past it.
 *
 * **It never prints a number it does not have.** With no position, or a position of
 * zero, the timecode is absent rather than `0:00` — the same rule that bans fabricated
 * loading counters and em-dash placeholders for missing metadata (§12.6).
 */

import type { JSX } from 'react';

import { hasCyrillic } from '@bl/shared';

import { Silkscreen } from '../../components/primitives/Silkscreen';
import { cx } from '../../components/primitives/Rule';

import styles from './states.module.css';

/** Real deck vocabulary, and one of §6.12's licensed silkscreen labels. */
const PANEL_LABEL = 'CUE';

export const RESUME_LABEL = 'Продолжить';
export const DISMISS_LABEL = 'Скрыть';

/**
 * `m:ss`, or `h:mm:ss` past an hour, with the minutes zero-padded only when an hour
 * is printed — the shape a deck readout has. Returns `null` for anything that is not
 * a real, finite, positive position, so the caller prints nothing at all.
 *
 * Every numeral in the app is JetBrains Mono with `tabular-nums`, so a timecode never
 * jitters as it counts (§4.3).
 */
export function formatTimecode(positionMs: number | null | undefined): string | null {
  if (typeof positionMs !== 'number' || !Number.isFinite(positionMs) || positionMs < 1000) {
    return null;
  }
  const total = Math.floor(positionMs / 1000);
  const seconds = total % 60;
  const minutes = Math.floor(total / 60) % 60;
  const hours = Math.floor(total / 3600);
  const ss = String(seconds).padStart(2, '0');
  if (hours > 0) return `${String(hours)}:${String(minutes).padStart(2, '0')}:${ss}`;
  return `${String(minutes)}:${ss}`;
}

export interface ResumeBannerProps {
  /** The track title from the pointer's `track` payload (`GET /api/playback`). */
  readonly title: string;
  /** Omitted entirely when the channel never gave the track a performer (§12.6). */
  readonly performer?: string | null | undefined;
  /** Resume position. No timecode is printed below one second. */
  readonly positionMs?: number | null | undefined;
  /**
   * Continues playback. **Must be reached from a real tap**: this is very often the
   * gesture that unlocks the audio element for the whole session (§13.2).
   */
  readonly onResume: () => void;
  /** Dismisses the banner. Omit it and no dismiss control is rendered. */
  readonly onDismiss?: (() => void) | undefined;
  readonly className?: string | undefined;
}

export function ResumeBanner({
  title,
  performer,
  positionMs,
  onResume,
  onDismiss,
  className,
}: ResumeBannerProps): JSX.Element {
  const timecode = formatTimecode(positionMs);
  const hasPerformer = typeof performer === 'string' && performer.trim() !== '';
  const meta = [hasPerformer ? performer : null, timecode].filter(
    (part): part is string => typeof part === 'string',
  );

  return (
    <section className={cx(styles.resume, className)} data-resume="true" aria-label={RESUME_LABEL}>
      <Silkscreen className={styles.resumeLabel}>{PANEL_LABEL}</Silkscreen>

      <p className={cx('t-ui', 't-17', 't-clamp-1', styles.resumeTitle)}>{title}</p>

      {meta.length === 0 ? null : (
        <p
          className={cx(
            't-mono',
            't-11',
            hasCyrillic(meta.join(' ')) && styles.cyr,
            styles.resumeMeta,
          )}
          data-resume-meta="true"
        >
          {meta.join(' · ')}
        </p>
      )}

      <div className={styles.resumeActions}>
        <button
          type="button"
          className={cx('t-ui', 't-17', styles.action, styles.primary)}
          onClick={onResume}
          data-action="resume"
        >
          {RESUME_LABEL}
        </button>
        {onDismiss === undefined ? null : (
          <button
            type="button"
            className={cx('t-ui', 't-17', styles.action)}
            onClick={onDismiss}
            data-action="dismiss"
          >
            {DISMISS_LABEL}
          </button>
        )}
      </div>
    </section>
  );
}
